import React, { ReactElement, useState } from 'react';
import { useDispatch } from 'react-redux';
import classNames from 'classnames';

import Button from '../../../../../components/buttons/Button';
import Icon from '../../../../../components/icons/Icon';
import ErrorToast from '../../../../../components/toast/ErrorToast';
import { fetchImageBlobFromUrl } from '../../content/utils/imageModalImageBlobUtils';
import { getBackgroundRemovedFilename } from './imageModalBackgroundRemovalFormatUtils';
import { removeBackground, uploadProcessedImage } from './imageModalBackgroundRemovalService';
import { updateElement } from '../../../../actions/elementActions';
import { cleanElementAttachment } from '../../../../attachments/attachmentActions';
import type { ImMNElement, MNImageContent } from '../../../../../../common/elements/elementModelTypes';
import {
    INDETERMINATE_BACKGROUND_REMOVAL_PROGRESS,
    type BackgroundRemovalProgress,
} from './imageModalBackgroundRemovalProgressTypes';
import {
    buildBackgroundRemovalToggleChanges,
    buildInitialBackgroundRemoval,
    hasBackgroundRemovalCache,
    isBackgroundRemovalActive,
} from './imageModalBackgroundRemovalUtils';

type ImageModalBackgroundRemovalButtonProps = {
    element: ImMNElement;
    elementId: string;
    cropSourceImage: MNImageContent;
    largestImageUrl: string;
    imageType: string;
    progress: BackgroundRemovalProgress | null;
    setProgress: (progress: BackgroundRemovalProgress | null) => void;
    disabled: boolean;
};

/**
 * Provide server-backed background removal that can toggle between original and processed image states.
 */
const ImageModalBackgroundRemovalButton: React.FC<ImageModalBackgroundRemovalButtonProps> = ({
    element,
    elementId,
    cropSourceImage,
    largestImageUrl,
    imageType,
    progress,
    setProgress,
    disabled,
}): ReactElement => {
    const dispatch = useDispatch();
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const handleFailure = (error: unknown) => {
        const message = error instanceof Error ? error.message : 'Background removal failed';
        setErrorMessage(message);
    };

    const setBackgroundRemovalActive = (active: boolean) => {
        const changes = buildBackgroundRemovalToggleChanges({ element, active });

        if (!changes) return;

        dispatch(updateElement({ id: elementId, changes }));
        dispatch(cleanElementAttachment({ id: elementId }));
    };

    const removeBackgroundFromImage = async () => {
        setErrorMessage(null);
        setProgress(INDETERMINATE_BACKGROUND_REMOVAL_PROGRESS);

        try {
            const imageBlob = await fetchImageBlobFromUrl(largestImageUrl);
            const fileName = getBackgroundRemovedFilename(largestImageUrl, imageType, imageBlob);
            const processedBlob = await removeBackground(imageBlob, fileName, setProgress);

            const withoutBackgroundSource = await uploadProcessedImage({
                dispatch,
                elementId,
                fileName,
                blob: processedBlob,
            });

            dispatch(
                updateElement({
                    id: elementId,
                    changes: {
                        image: withoutBackgroundSource,
                        backgroundRemoval: buildInitialBackgroundRemoval({
                            withBackgroundSource: cropSourceImage,
                            withoutBackgroundSource,
                        }),
                    },
                }),
            );
        } catch (error) {
            handleFailure(error);
        } finally {
            setProgress(null);
        }
    };

    const handleButtonClick = () => {
        if (isBackgroundRemovalActive(element)) return setBackgroundRemovalActive(false);

        if (hasBackgroundRemovalCache(element)) return setBackgroundRemovalActive(true);

        return removeBackgroundFromImage();
    };

    const isProcessing = !!progress;
    const isDisabled = disabled || isProcessing;
    const isBackgroundRemovalApplied = hasBackgroundRemovalCache(element);
    const isBackgroundRemovalCurrentlyRemoved = isBackgroundRemovalActive(element);
    const title = isBackgroundRemovalCurrentlyRemoved ? 'Apply background' : 'Remove background';

    return (
        <>
            <Button
                className={classNames(
                    'StyledButton secondary image-control whitespace-nowrap px-[1.6rem] py-0',
                    isBackgroundRemovalApplied &&
                        'bg-[var(--ui-modal-background-hover)] text-[var(--ui-modal-foreground-primary)]',
                )}
                onClickFn={handleButtonClick}
                disabled={isDisabled}
                title={title}
            >
                <span className="inline-flex items-center gap-[0.8rem] whitespace-nowrap">
                    <Icon name={isBackgroundRemovalApplied ? 'checkbox-checked' : 'checkbox-unchecked'} size="SMALL" />
                    <span>{title}</span>
                </span>
            </Button>
            <ErrorToast
                show={!!errorMessage}
                message={errorMessage}
                groupId="ImageModalV2"
                onDismiss={() => setErrorMessage(null)}
            />
        </>
    );
};

export default ImageModalBackgroundRemovalButton;
